/**
 * GMA-1624 (CompassCare Web — SITRep Board) — Nora situation-report board.
 *
 * Mirrors the hi-fi mockup (CompassCare SITRep Board.dc.html): one data model,
 * two audiences, three cadences. A client rail opens a per-client report;
 * the clinician rendering carries the mockup's document anatomy (masthead with
 * rolled-up OVERALL status + CONFIDENCE, BLUF, expandable Key Developments
 * with Δ-vs-baseline + priority + confidence + click-for-sources, Operational
 * Highlights, Risk Implications, Indicators to Watch, Recommended Actions
 * across three horizons, and the Nora Confidence Assessment legend + feeds);
 * the family rendering maps to plain-language sections with no clinician
 * jargon (AC — guarded by noraSitrepModel.hasClinicianJargon in tests).
 *
 * The report STRUCTURE is rendered from data; the prose is demo fixture text —
 * the real SITRep prose is generated by Nora on the backend (the GMA-1624
 * follow-up), so Export PDF / Send… / Regenerate queue honest stub notes
 * (same pattern as IntelPage's SITRep button note). Rollups reuse the REAL
 * engine: overallStatus for the masthead RAG and resolveConfidence per
 * observation (a device-offline window forces LOW — AC). Fail-safe: missing
 * or malformed sitrep data renders a calm empty state, never a throw.
 */
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { theme, severityToken } from "../../styles/designTokens";
import {
  CADENCES,
  resolveConfidence,
  overallStatus,
} from "./noraSitrepModel";

// Engine priority → the mockup's GREEN/AMBER/RED chip, on the platform's
// shared severity palette (overallStatus maps the same way: critical→red,
// watch→amber, everything else→green). Fail-safe: unknown → benign GREEN.
export function developmentLevel(priority) {
  if (priority === "critical") {
    return { label: "RED", color: severityToken("critical").color };
  }
  if (priority === "watch") {
    return { label: "AMBER", color: severityToken("caution").color };
  }
  return { label: "GREEN", color: severityToken("normal").color };
}

const CONFIDENCE_COLOR = {
  high: severityToken("normal").color,
  moderate: severityToken("caution").color,
  low: severityToken("critical").color,
};

/**
 * Masthead confidence rollup from the developments' observations: any LOW
 * observation (e.g. a device-offline window) taints the whole report LOW;
 * otherwise HIGH when direct telemetry/logs carry at least half the
 * observations, else MODERATE. Empty/invalid input is LOW (conservative,
 * matching the engine's fail-safe posture).
 */
export function overallConfidence(developments) {
  const list = Array.isArray(developments) ? developments.filter(Boolean) : [];
  if (!list.length) return "low";
  const labels = list.map((d) => resolveConfidence(d));
  if (labels.includes("low")) return "low";
  const high = labels.filter((l) => l === "high").length;
  return high * 2 >= labels.length ? "high" : "moderate";
}

function SectionHeading({ children }) {
  return (
    <h3
      style={{
        margin: 0,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 1,
        textTransform: "uppercase",
        color: theme.textMuted,
      }}
    >
      {children}
    </h3>
  );
}

SectionHeading.propTypes = { children: PropTypes.node };

function Chip({ children, color, testId }) {
  return (
    <span
      data-testid={testId}
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 6,
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: 0.6,
        whiteSpace: "nowrap",
        color: color || theme.textMuted,
        background: `${color || theme.textMuted}1f`,
        border: `1px solid ${theme.border}`,
      }}
    >
      {children}
    </span>
  );
}

Chip.propTypes = {
  children: PropTypes.node,
  color: PropTypes.string,
  testId: PropTypes.string,
};

function ToolbarButton({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={active === undefined ? undefined : !!active}
      onClick={onClick}
      style={{
        padding: "6px 12px",
        borderRadius: 8,
        cursor: "pointer",
        border: `1px solid ${active ? severityToken("nora").color : theme.border}`,
        background: active ? `${severityToken("nora").color}1f` : "transparent",
        color: active ? theme.textPrimary : theme.textMuted,
        font: `600 12px ${theme.fontUI}`,
      }}
    >
      {label}
    </button>
  );
}

ToolbarButton.propTypes = {
  label: PropTypes.node.isRequired,
  active: PropTypes.bool,
  onClick: PropTypes.func,
  testId: PropTypes.string,
};

function DevelopmentRow({ dev, open, onToggle }) {
  const level = developmentLevel(dev.priority);
  const conf = resolveConfidence(dev);
  return (
    <li data-testid="sitrep-dev-row" style={{ listStyle: "none" }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        style={{
          display: "grid",
          gap: 6,
          width: "100%",
          textAlign: "left",
          padding: "10px 12px",
          borderRadius: 10,
          cursor: "pointer",
          background: theme.bg,
          border: `${open ? 2 : 1}px solid ${open ? level.color : theme.border}`,
          color: theme.textPrimary,
          font: `13px ${theme.fontUI}`,
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <strong style={{ flex: 1, minWidth: 120, fontSize: 13 }}>
            {open ? "▾" : "▸"} {dev.title}
          </strong>
          <Chip testId="sitrep-dev-level" color={level.color}>
            {level.label}
          </Chip>
          <Chip testId="sitrep-dev-confidence" color={CONFIDENCE_COLOR[conf]}>
            {conf.toUpperCase()} CONFIDENCE
          </Chip>
        </span>
        <span style={{ color: theme.textMuted, fontSize: 12.5, lineHeight: 1.45 }}>
          {dev.text}
        </span>
        {open ? (
          <span style={{ display: "grid", gap: 4, fontSize: 12 }}>
            <span>
              <strong style={{ color: theme.textMuted }}>Δ baseline:</strong>{" "}
              {dev.delta}
            </span>
            <span style={{ color: severityToken("nora").color }}>
              <strong style={{ color: theme.textMuted }}>Sources:</strong>{" "}
              {(dev.sources || []).join(" · ")}
            </span>
          </span>
        ) : null}
      </button>
    </li>
  );
}

DevelopmentRow.propTypes = {
  dev: PropTypes.object.isRequired,
  open: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

const panelStyle = {
  border: `1px solid ${theme.border}`,
  borderRadius: 10,
  padding: 12,
  display: "grid",
  gap: 8,
};

function ClinicianDocument({ entry, doc, sitrep }) {
  const [openDev, setOpenDev] = useState(-1);
  const status = overallStatus(doc.developments);
  const statusToken = severityToken(
    status === "red" ? "critical" : status === "amber" ? "caution" : "normal",
  );
  const confidence = overallConfidence(doc.developments);
  const confLabels = (doc.developments || []).map((d) => resolveConfidence(d));
  const directCount = confLabels.filter((l) => l === "high").length;

  return (
    <article style={{ display: "grid", gap: 14 }}>
      <header
        data-testid="sitrep-masthead"
        style={{
          display: "grid",
          gap: 6,
          borderBottom: `1px solid ${theme.border}`,
          paddingBottom: 12,
        }}
      >
        <span
          style={{
            fontSize: 10.5,
            letterSpacing: 1.4,
            color: theme.textMuted,
            fontFamily: theme.fontMono,
          }}
        >
          GENERATED BY NORA | COMPASSCARE CLIENT SITREP
        </span>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <h2 style={{ margin: 0, fontSize: 17 }}>{entry.clientName}</h2>
          <span style={{ color: theme.textMuted, fontSize: 12.5 }}>
            {[
              entry.age ? `${entry.age}` : null,
              entry.livingEnv === "group_home"
                ? `${entry.home}${entry.room ? ` · ${entry.room}` : ""}`
                : "Independent living",
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 12.5, color: theme.textMuted }}>
            {doc.mastTitle} · {doc.windowLabel}
          </span>
          <Chip testId="sitrep-overall" color={statusToken.color}>
            OVERALL: {status.toUpperCase()}
          </Chip>
          <Chip testId="sitrep-confidence" color={CONFIDENCE_COLOR[confidence]}>
            CONFIDENCE: {confidence.toUpperCase()}
          </Chip>
        </div>
      </header>

      <section aria-label="Bottom line up front" style={panelStyle}>
        <SectionHeading>BLUF — Bottom Line Up Front</SectionHeading>
        <p data-testid="sitrep-bluf" style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55 }}>
          {doc.bluf}
        </p>
      </section>

      <section aria-label="Key developments" style={{ display: "grid", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          <SectionHeading>Key Developments</SectionHeading>
          <span style={{ fontSize: 11, color: theme.textMuted }}>
            click a row for sources · every item = change vs. baseline + priority + confidence
          </span>
        </div>
        <ul style={{ margin: 0, padding: 0, display: "grid", gap: 8 }}>
          {doc.developments.map((dev, i) => (
            <DevelopmentRow
              key={dev.id || i}
              dev={dev}
              open={openDev === i}
              onToggle={() => setOpenDev(openDev === i ? -1 : i)}
            />
          ))}
        </ul>
      </section>

      <section aria-label="Operational highlights" style={panelStyle}>
        <SectionHeading>Operational Highlights</SectionHeading>
        {doc.highlights.map((h) => (
          <p key={h.label} style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5 }}>
            <strong>{h.label}</strong>
            <span style={{ color: theme.textMuted }}> — {h.text}</span>
          </p>
        ))}
      </section>

      <section aria-label="Risk implications" style={panelStyle}>
        <SectionHeading>Risk Implications</SectionHeading>
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12.5 }}>
          <thead>
            <tr>
              {["Area", "Status", "Assessment"].map((h) => (
                <th
                  key={h}
                  scope="col"
                  style={{
                    textAlign: "left",
                    padding: "4px 8px 4px 0",
                    color: theme.textMuted,
                    fontSize: 10.5,
                    letterSpacing: 0.8,
                    textTransform: "uppercase",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {doc.risks.map((r) => (
              <tr key={r.area} data-testid="sitrep-risk-row">
                <td style={{ padding: "5px 8px 5px 0" }}>{r.area}</td>
                <td style={{ padding: "5px 8px 5px 0" }}>
                  <Chip color={severityToken(r.tone).color}>{r.status}</Chip>
                </td>
                <td style={{ padding: "5px 0", color: theme.textMuted, lineHeight: 1.4 }}>
                  {r.why}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section aria-label="Indicators to watch" style={panelStyle}>
        <SectionHeading>Indicators to Watch</SectionHeading>
        {doc.watch.map((w) => (
          <p key={w.k} style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5 }}>
            <span aria-hidden="true" style={{ color: severityToken("caution").color }}>
              ⚑{" "}
            </span>
            <strong>{w.k}:</strong>
            <span style={{ color: theme.textMuted }}> {w.t}</span>
          </p>
        ))}
      </section>

      <section aria-label="Recommended actions" style={panelStyle}>
        <SectionHeading>Recommended Actions</SectionHeading>
        {[
          ["immediate", doc.actions.now, severityToken("critical").color],
          ["next shift / today", doc.actions.today, severityToken("atRisk").color],
          ["24–72 hours", doc.actions.later, severityToken("caution").color],
        ].map(([horizon, text, color]) => (
          <div key={horizon} style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
            <span
              style={{
                flex: "none",
                minWidth: 110,
                textAlign: "center",
                padding: "3px 7px",
                borderRadius: 6,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.7,
                textTransform: "uppercase",
                color,
                background: `${color}1a`,
              }}
            >
              {horizon}
            </span>
            <span style={{ fontSize: 12.5, lineHeight: 1.45 }}>{text}</span>
          </div>
        ))}
      </section>

      <section aria-label="Nora confidence assessment" style={panelStyle}>
        <SectionHeading>Nora Confidence Assessment</SectionHeading>
        {(sitrep.confidenceLegend || []).map((l) => (
          <p key={l.level} style={{ margin: 0, fontSize: 12, lineHeight: 1.5 }}>
            <strong style={{ color: CONFIDENCE_COLOR[l.level] }}>
              {String(l.level).toUpperCase()}
            </strong>
            <span style={{ color: theme.textMuted }}> — {l.text}</span>
          </p>
        ))}
        <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5 }}>
          Overall confidence for this SitRep:{" "}
          <strong style={{ color: CONFIDENCE_COLOR[confidence] }}>
            {confidence.toUpperCase()}
          </strong>
          <span style={{ color: theme.textMuted }}>
            {" "}
            — {directCount} of {confLabels.length} observations from direct
            telemetry/logs.
          </span>
        </p>
        <p style={{ margin: 0, fontSize: 11.5, color: theme.textMuted, lineHeight: 1.5 }}>
          Feeds: {(sitrep.feeds || []).join(" · ")}
        </p>
      </section>
    </article>
  );
}

ClinicianDocument.propTypes = {
  entry: PropTypes.object.isRequired,
  doc: PropTypes.object.isRequired,
  sitrep: PropTypes.object.isRequired,
};

function FamilyList({ heading, items, marker }) {
  return (
    <section aria-label={heading} style={panelStyle}>
      <SectionHeading>{heading}</SectionHeading>
      {items.map((text) => (
        <p key={text} style={{ margin: 0, fontSize: 13, lineHeight: 1.55 }}>
          <span aria-hidden="true" style={{ color: severityToken("normal").color }}>
            {marker}{" "}
          </span>
          {text}
        </p>
      ))}
    </section>
  );
}

FamilyList.propTypes = {
  heading: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
  marker: PropTypes.string.isRequired,
};

function FamilyDocument({ entry, doc }) {
  const fam = doc.family || {};
  return (
    <article data-testid="sitrep-family" style={{ display: "grid", gap: 14 }}>
      <header style={{ display: "grid", gap: 4, borderBottom: `1px solid ${theme.border}`, paddingBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 17 }}>{fam.title}</h2>
        <span style={{ color: theme.textMuted, fontSize: 12.5 }}>
          {doc.windowLabel} · from Nora, {entry.clientName.split(" ")[0]}&apos;s
          CompassCare assistant
        </span>
        <strong style={{ fontSize: 13.5 }}>{fam.status}</strong>
      </header>
      <section aria-label="The short version" style={panelStyle}>
        <SectionHeading>The short version</SectionHeading>
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55 }}>{fam.bluf}</p>
      </section>
      <FamilyList heading="What went well" items={fam.well || []} marker="✓" />
      <FamilyList
        heading="Things to keep an eye on"
        items={fam.watch || []}
        marker="⚑"
      />
      <FamilyList heading="What we're doing" items={fam.doing || []} marker="•" />
      <FamilyList heading="How you can help" items={fam.help || []} marker="•" />
      <section aria-label="How sure are we" style={panelStyle}>
        <SectionHeading>How sure are we about all this?</SectionHeading>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: theme.textMuted }}>
          {fam.confidence}
        </p>
      </section>
      <p style={{ margin: 0, fontSize: 12, color: theme.textMuted }}>
        Questions? Reply here or call the CompassCare care line anytime.
      </p>
    </article>
  );
}

FamilyDocument.propTypes = {
  entry: PropTypes.object.isRequired,
  doc: PropTypes.object.isRequired,
};

export default function SitrepPage({
  sitrep = null,
  initialClientId = null,
  initialCadence = "daily",
  provenance = null,
}) {
  const entries = useMemo(
    () =>
      sitrep && Array.isArray(sitrep.clients)
        ? sitrep.clients.filter((c) => c && c.cadences)
        : [],
    [sitrep],
  );
  const [audience, setAudience] = useState("clinician");
  const [cadence, setCadence] = useState(
    CADENCES.includes(initialCadence) ? initialCadence : "daily",
  );
  const [selectedId, setSelectedId] = useState(initialClientId);
  const [actionNote, setActionNote] = useState(null);

  const selected =
    entries.find((c) => c.clientId === selectedId) || entries[0] || null;
  const doc = selected ? selected.cadences[cadence] : null;

  return (
    <div
      data-testid="sitrep-page"
      style={{
        fontFamily: theme.fontUI,
        color: theme.textPrimary,
        display: "grid",
        gap: 12,
        padding: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontSize: 11, color: theme.textMuted, letterSpacing: 0.6 }}>
          audience
        </span>
        <ToolbarButton
          testId="sitrep-audience-clinician"
          label="Clinician / Professional"
          active={audience === "clinician"}
          onClick={() => setAudience("clinician")}
        />
        <ToolbarButton
          testId="sitrep-audience-family"
          label="Family / Caregiver (simplified)"
          active={audience === "family"}
          onClick={() => setAudience("family")}
        />
        <span
          style={{
            fontSize: 11,
            color: theme.textMuted,
            letterSpacing: 0.6,
            marginLeft: 8,
          }}
        >
          cadence
        </span>
        {CADENCES.map((key) => (
          <ToolbarButton
            key={key}
            testId={`sitrep-cadence-${key}`}
            label={key.charAt(0).toUpperCase() + key.slice(1)}
            active={cadence === key}
            onClick={() => setCadence(key)}
          />
        ))}
        <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {["Export PDF", "Send…", "Regenerate"].map((label) => (
            <ToolbarButton
              key={label}
              label={label}
              onClick={() =>
                setActionNote(
                  `${label.replace("…", "")} wires to the Nora backend and the delivery audit trail — GMA-1624 follow-up.`,
                )
              }
            />
          ))}
        </span>
      </div>

      {actionNote ? (
        <span
          data-testid="sitrep-action-note"
          style={{ color: theme.textMuted, fontSize: 12 }}
        >
          {actionNote}
        </span>
      ) : null}

      {provenance && provenance.watchTitle ? (
        <span
          data-testid="sitrep-provenance"
          style={{ color: severityToken("nora").color, fontSize: 12 }}
        >
          Generated from Intel watch: {provenance.watchTitle}
        </span>
      ) : null}

      {entries.length === 0 ? (
        <p
          data-testid="sitrep-empty"
          style={{ margin: 0, color: theme.textMuted, fontSize: 13, lineHeight: 1.6 }}
        >
          No SITReps generated yet. Nora composes the report prose on the
          backend — live generation wires up as the GMA-1624 follow-up. When it
          lands, every client&apos;s daily, weekly, and monthly reports appear
          here for both audiences.
        </p>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(220px, 300px) minmax(0, 1fr)",
            gap: 12,
            alignItems: "start",
          }}
        >
          <div data-testid="sitrep-client-rail" style={{ display: "grid", gap: 6 }}>
            {entries.map((entry) => {
              const isSelected = selected && selected.clientId === entry.clientId;
              const entryDoc = entry.cadences[cadence];
              const rag = overallStatus(entryDoc ? entryDoc.developments : []);
              const ragToken = severityToken(
                rag === "red" ? "critical" : rag === "amber" ? "caution" : "normal",
              );
              return (
                <button
                  key={entry.clientId}
                  type="button"
                  data-testid="sitrep-client-card"
                  aria-current={isSelected ? "true" : undefined}
                  onClick={() => setSelectedId(entry.clientId)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    textAlign: "left",
                    padding: "9px 11px",
                    borderRadius: 10,
                    cursor: "pointer",
                    background: isSelected ? theme.surface : "transparent",
                    border: `1px solid ${isSelected ? ragToken.color : theme.border}`,
                    color: theme.textPrimary,
                    font: `13px ${theme.fontUI}`,
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      flex: "none",
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: ragToken.color,
                    }}
                  />
                  <span style={{ flex: 1, minWidth: 0 }}>{entry.clientName}</span>
                  <Chip color={ragToken.color}>{rag.toUpperCase()}</Chip>
                </button>
              );
            })}
          </div>

          <div
            data-testid="sitrep-document"
            style={{
              border: `1px solid ${theme.border}`,
              borderRadius: 10,
              padding: 16,
              background: theme.surface,
              minHeight: 320,
            }}
          >
            {doc ? (
              audience === "family" ? (
                <FamilyDocument entry={selected} doc={doc} />
              ) : (
                <ClinicianDocument entry={selected} doc={doc} sitrep={sitrep} />
              )
            ) : (
              <span style={{ color: theme.textMuted, fontSize: 12.5 }}>
                Select a client to open their report.
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

SitrepPage.propTypes = {
  /** getDemoSitrep()-shaped data; null renders the calm empty state. */
  sitrep: PropTypes.shape({
    feeds: PropTypes.arrayOf(PropTypes.string),
    confidenceLegend: PropTypes.arrayOf(PropTypes.object),
    clients: PropTypes.arrayOf(PropTypes.object),
  }),
  /** Client to open first (e.g. the Intel watch's first impacted client). */
  initialClientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  initialCadence: PropTypes.oneOf(["daily", "weekly", "monthly"]),
  /** Where this board was opened from (Intel watch), for the provenance line. */
  provenance: PropTypes.shape({
    watchId: PropTypes.string,
    watchTitle: PropTypes.string,
  }),
};
